import { useState } from "react";
import { MAX_RESERVE_TAGS, normaliseTags, reserveTagLine, type ReserveTag } from "@iltocat/shared";
import { Icon } from "./Icon.js";

/**
 * Reserve tags, the way a person sees them everywhere: a tag icon and the
 * name. The icon is the whole point -- it says "a reserve matches on this" on
 * an item, on a receipt line and on a reserve's own card, so nobody has to
 * wonder why one tag counts toward back stock and another does not.
 */
export function ReserveTagChip({ name, onRemove }: { name: string; onRemove?: () => void }) {
  return (
    <span className="reserve-tag">
      <Icon name="tag" size={12} />
      {name}
      {onRemove && (
        <button
          type="button"
          className="reserve-tag-remove"
          aria-label={`Remove reserve tag ${name}`}
          onClick={onRemove}
        >
          ×
        </button>
      )}
    </span>
  );
}

/**
 * Click to pick, not type to match: every reserve tag is a toggle, and the
 * choice reads back as a sentence ("17-inch and 256 GB") so an AND rule says
 * what it means. `onCreate`, when given, offers a new reserve tag inline --
 * the reserve form is where they are made; the receiving screen only picks.
 *
 * A stockroom can have dozens, and a wall of pills is hard to read. So an
 * unpicked tag is just its name (the field already says what they are), and
 * past `SHOWN` tags the rest fold behind "N more", with a box to find one by
 * name. A picked tag is always shown, folded or not.
 */
const SHOWN = 10;

export function ReserveTagPicker({
  tags,
  selected,
  onChange,
  onCreate,
  label,
  lead,
  max = MAX_RESERVE_TAGS,
}: {
  tags: ReserveTag[];
  selected: string[];
  onChange: (next: string[]) => void;
  onCreate?: (name: string) => Promise<string | null>;
  label: string;
  /** What the sentence under the chips starts with, e.g. "Matches stock tagged". */
  lead: string;
  max?: number;
}) {
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [find, setFind] = useState("");
  const [unfolded, setUnfolded] = useState(false);
  const chosen = new Set(selected.map((name) => name.toLowerCase()));
  const full = selected.length >= max;
  const many = tags.length > SHOWN;
  const needle = find.trim().toLowerCase();
  const shown = needle
    ? tags.filter((tag) => tag.name.toLowerCase().includes(needle))
    : many && !unfolded
      ? tags.filter((tag, index) => index < SHOWN || chosen.has(tag.name.toLowerCase()))
      : tags;
  const folded = needle ? 0 : tags.length - shown.length;

  const toggle = (name: string) =>
    onChange(
      chosen.has(name.toLowerCase())
        ? selected.filter((one) => one.toLowerCase() !== name.toLowerCase())
        : [...selected, name],
    );

  async function create() {
    const [name] = normaliseTags([text]);
    if (!name || !onCreate) return;
    setBusy(true);
    const made = await onCreate(name);
    setBusy(false);
    if (made) {
      setText("");
      if (!chosen.has(made.toLowerCase())) onChange([...selected, made]);
    }
  }

  return (
    <div className="field">
      <span className="field-label">{label}</span>
      {tags.length === 0 && !onCreate ? (
        <p className="muted small">
          No reserve tags yet. They are made on the Reserves screen, with the reserves that match on them.
        </p>
      ) : (
        <>
          {many && (
            <input
              className="input input-small reserve-pick-find"
              type="search"
              aria-label={`Find a reserve tag, of ${tags.length}`}
              placeholder={`Find among ${tags.length}`}
              value={find}
              onChange={(event) => setFind(event.target.value)}
            />
          )}
          <div className="reserve-picker" role="group" aria-label={label}>
            {needle && shown.length === 0 && <span className="muted small">None called that.</span>}
            {shown.map((tag) => {
              const on = chosen.has(tag.name.toLowerCase());
              return (
                <button
                  key={tag.name}
                  type="button"
                  className={on ? "reserve-pick reserve-pick-on" : "reserve-pick"}
                  aria-pressed={on}
                  disabled={!on && full}
                  onClick={() => toggle(tag.name)}
                >
                  {on && <Icon name="check" size={13} />}
                  {tag.name}
                  <span className="reserve-pick-count" title={`${tag.itemCount} items wear it`}>
                    {tag.itemCount}
                  </span>
                </button>
              );
            })}
            {folded > 0 && (
              <button type="button" className="reserve-pick-more" onClick={() => setUnfolded(true)}>
                {folded} more
              </button>
            )}
            {many && unfolded && !needle && (
              <button type="button" className="reserve-pick-more" onClick={() => setUnfolded(false)}>
                Show fewer
              </button>
            )}
            {onCreate && (
              <span className="reserve-pick-new">
                <input
                  className="input input-small"
                  aria-label="A new reserve tag"
                  placeholder="New reserve tag"
                  value={text}
                  disabled={busy || full}
                  onChange={(event) => setText(event.target.value)}
                  onKeyDown={(event) => {
                    if (event.key === "Enter") {
                      event.preventDefault();
                      void create();
                    }
                  }}
                />
                <button
                  type="button"
                  className="button button-quiet button-small"
                  disabled={busy || !text.trim() || full}
                  onClick={() => void create()}
                >
                  <Icon name="plus" size={14} />
                  Add
                </button>
              </span>
            )}
          </div>
        </>
      )}
      <p className="reserve-picker-says">
        {selected.length === 0 ? (
          <span className="muted small">Nothing picked yet.</span>
        ) : (
          <>
            <span className="muted small">{lead} </span>
            <strong>{reserveTagLine(selected)}</strong>
          </>
        )}
      </p>
    </div>
  );
}
